import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BarChart2, Star, Shield, Zap, Users, Download } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import Button from '../components/ui/Button';
import Icon from '../components/ui/Icon';
import ToolCard from '../components/ui/ToolCard';
import CategoryFilters from '../components/ui/CategoryFilters';
import { useTheme } from '../contexts/ThemeContext';
import categories from '../data/categories';
import { getToolsByCategory } from '../data/tools';

/**
 * Компонент HomePage - главная страница сайта
 */
const HomePage = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const [activeCategory, setActiveCategory] = useState('popular');
  const navigate = useNavigate();
  
  // Получаем инструменты для активной категории
  const tools = getToolsByCategory(activeCategory);
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200">
      {/* Фоновый узор */}
      <div className="fixed inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)] [background-size:20px_20px] opacity-25 pointer-events-none"></div>
      
      {/* Навигационная панель */}
      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      
      {/* Основное содержимое */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero секция */}
        <div className="mb-12">
          <div className="rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
            <div className="px-6 py-12 sm:px-12 sm:py-16 flex flex-col lg:flex-row items-center">
              <div className="lg:w-1/2 mb-8 lg:mb-0 text-center lg:text-left">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                  Все необходимые расчеты в одном месте
                </h1>
                
                <p className="text-lg sm:text-xl text-indigo-100 mb-6 max-w-xl mx-auto lg:mx-0">
                  Gensio — бесплатная платформа с более чем 300 инструментами для ежедневных задач
                </p>
                
                <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                  <Button 
                    primary 
                    className="px-6 py-3 text-lg bg-indigo-400 text-indigo-600 hover:bg-gray-100"
                    onClick={() => navigate('/category/popular')}
                  >
                    Начать пользоваться
                  </Button>
                  
                  <Button 
                    className="px-6 py-3 text-lg bg-white/10 text-white border-white hover:bg-black/10"
                    onClick={() => navigate('/about')}
                  >
                    Узнать больше
                  </Button>
                </div>
              </div>
              
              <div className="lg:w-1/2 flex justify-center lg:justify-end">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex flex-col items-center text-center">
                      <Icon name="calculator" color="gray" className="text-white mb-2" size="lg" />
                      <p className="text-sm font-medium">Калькуляторы</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex flex-col items-center text-center">
                      <Icon name="currency" color="gray" className="text-white mb-2" size="lg" />
                      <p className="text-sm font-medium">Конвертеры</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex flex-col items-center text-center">
                      <Icon name="calendar" color="gray" className="text-white mb-2" size="lg" />
                      <p className="text-sm font-medium">Дата и время</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex flex-col items-center text-center">
                      <Icon name="key" color="gray" className="text-white mb-2" size="lg" />
                      <p className="text-sm font-medium">Генераторы</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Статистика */}
        <div className="mb-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center border border-gray-100 dark:border-gray-700">
              <div className="inline-flex p-3 rounded-lg bg-indigo-50 dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 mb-3">
                <Icon name="calculator" size="lg" />
              </div>
              <div className="text-3xl font-bold mb-1 text-indigo-600 dark:text-indigo-400">
                300+
              </div>
              <p className="text-gray-600 dark:text-gray-400">Инструментов</p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center border border-gray-100 dark:border-gray-700">
              <div className="inline-flex p-3 rounded-lg bg-purple-50 dark:bg-gray-700 text-purple-600 dark:text-purple-400 mb-3">
                <Users className="h-6 w-6" />
              </div>
              <div className="text-3xl font-bold mb-1 text-purple-600 dark:text-purple-400">
                1M+
              </div>
              <p className="text-gray-600 dark:text-gray-400">Пользователей</p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center border border-gray-100 dark:border-gray-700">
              <div className="inline-flex p-3 rounded-lg bg-fuchsia-50 dark:bg-gray-700 text-fuchsia-600 dark:text-fuchsia-400 mb-3">
                <BarChart2 className="h-6 w-6" />
              </div>
              <div className="text-3xl font-bold mb-1 text-fuchsia-600 dark:text-fuchsia-400">
                10M+
              </div>
              <p className="text-gray-600 dark:text-gray-400">Расчетов</p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center border border-gray-100 dark:border-gray-700">
              <div className="inline-flex p-3 rounded-lg bg-emerald-50 dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 mb-3">
                <Star className="h-6 w-6" />
              </div>
              <div className="text-3xl font-bold mb-1 text-emerald-600 dark:text-emerald-400">
                4.9
              </div>
              <p className="text-gray-600 dark:text-gray-400">Рейтинг</p>
            </div>
          </div>
        </div>
        
        {/* Фильтры категорий */}
        <CategoryFilters 
          categories={categories} 
          activeCategory={activeCategory} 
          setActiveCategory={setActiveCategory}
        />
        
        {/* Инструменты по категориям */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            {categories.find(c => c.id === activeCategory)?.name}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((tool) => (
              <ToolCard 
                key={tool.id} 
                tool={tool}
              />
            ))}
          </div>
        </div>
        
        {/* Секция преимуществ */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Почему выбирают <span className="text-indigo-600 dark:text-indigo-400">Gensio</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-cyan-50 dark:bg-gray-700 text-cyan-600 dark:text-cyan-400 mb-4">
                <Zap className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                Высокая скорость
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Мгновенные результаты без перезагрузки страницы и ожидания
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-indigo-50 dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 mb-4">
                <Users className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                Удобный интерфейс
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Интуитивно понятный дизайн и быстрый доступ к нужным инструментам
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-emerald-50 dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 mb-4">
                <Shield className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                Точность расчетов
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Проверенные алгоритмы и формулы для безошибочных вычислений
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-fuchsia-50 dark:bg-gray-700 text-fuchsia-600 dark:text-fuchsia-400 mb-4">
                <Download className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                Полностью бесплатно
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Все инструменты бесплатны для использования, без скрытых платежей
              </p>
            </div>
          </div>
        </div>
        
        {/* CTA блок */}
        <div className="mb-16">
          <div className="rounded-2xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 text-white">            
            <div className="px-6 py-12 sm:px-12 sm:py-16 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Начните пользоваться прямо сейчас
              </h2>
              
              <p className="text-purple-100 mb-8 max-w-2xl mx-auto text-lg">
                Gensio полностью бесплатен и не требует регистрации. Используйте все инструменты без ограничений!
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center">
                <Button 
                  primary 
                  className="px-8 py-3 text-lg bg-gray-400 text-indigo-600 hover:bg-gray-100"
                  onClick={() => navigate('/category/popular')}
                >
                  Начать пользоваться
                </Button>
                
                <Button 
                  className="px-8 py-3 text-lg bg-white/10 text-white border-white hover:bg-black/10"
                  onClick={() => navigate('/category/popular')}
                >
                  Популярные инструменты
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Футер */}
      <Footer />
    </div>
  );
};

export default HomePage;